// src/ExpertCheckout.jsx

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const ExpertCheckout = () => {
  const [services, setServices] = useState([]);
  const [checkoutLinks, setCheckoutLinks] = useState([]);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const { accessToken } = useContext(AuthContext);

  useEffect(() => {
    if (accessToken) {
      fetchServices();
      fetchCheckoutLinks();
    }
  }, [accessToken]);

  const fetchServices = async () => {
    try {
      const response = await axios.get('https://quirkleai.com/api/services/', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchCheckoutLinks = async () => {
    try {
      const response = await axios.get('https://quirkleai.com/api/checkout-links/', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      setCheckoutLinks(response.data);
    } catch (error) {
      console.error('Error fetching checkout links:', error);
    }
  };

  const handleGenerateLink = async (serviceId) => {
    try {
      await axios.post('https://quirkleai.com/api/checkout-links/', { service_id: serviceId }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      fetchCheckoutLinks(); // Refresh the links list
    } catch (error) {
      console.error('Error generating checkout link:', error);
      alert('Failed to generate link. A link for this service may already exist.');
    }
  };

  const handleCopyLink = (link, serviceId) => {
    navigator.clipboard.writeText(link);
    setCopiedLinkId(serviceId);
    setTimeout(() => {
      setCopiedLinkId(null);
    }, 2000);
  };

  const getLinkForService = (serviceId) => {
    return checkoutLinks.find(link => link.service.id === serviceId);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Checkout Links</h1>

      <Card className="bg-white shadow-sm border">
        <CardContent className="py-6">
          <p className="mb-4 text-gray-600">
            Generate and manage direct, shareable links for your services.
          </p>
          
          {services.length === 0 ? (
            <p className="text-gray-500 text-center py-4">You haven't created any services yet. Please add services from the 'Your Services' page first.</p>
          ) : (
            <div className="space-y-3">
              {services.map((service) => {
                const link = getLinkForService(service.id);
                return (
                  <div key={service.id} className="text-sm text-gray-700 border rounded p-3 bg-gray-50 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-gray-500">₹{service.price} • {service.duration}</p>
                      {link && (
                        <div className="mt-2">
                          <code className="text-xs text-blue-600">{link.link}</code>
                        </div>
                      )}
                    </div>
                    {link ? (
                      <Button size="sm" variant="ghost" onClick={() => handleCopyLink(link.link, service.id)}>
                        {copiedLinkId === service.id ? 'Copied!' : 'Copy Link'}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleGenerateLink(service.id)}>
                        + Generate Link
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpertCheckout;
